// ItemName — how a catalog item is written down, everywhere.
//
// WHY THIS EXISTS
// Every operational surface used to print the SKU: FF-PYR-02-1-CH in the PO
// line, in the receive drawer, on the inventory row, in the toast that
// confirmed the work. People in the shop read those as "confusing number
// letter combos" and kept a paper cheat sheet. The catalog now carries a human
// NAME, and the API joins three fields onto every catalog-joined read:
//
//   base_name      "Gen2 Pyro"                    the thing
//   variant_detail "V24, rev 1, Green Circuits"   which one of the things
//   display_name   base_name + " (variant_detail)"  the two, already joined
//
// So this component is the ONE answer to "print this item", and the direction
// it encodes is: name first, SKU only on detail pages and labels. A surface
// that wants the SKU asks for it (`showSku`); nothing gets it by accident.
//
// ── THE THREE FIELDS ARE NOT INTERCHANGEABLE ────────────────────────────────
// Rendering prefers `base_name` so the variant detail can be styled one step
// down as its own span. `display_name` is the fallback for a payload that
// carries only the joined string, and it is rendered AS IS — appending
// variant_detail to it would print the detail twice.
//
// `itemLabel()` is the other direction: one flat string for a title attribute,
// a toast, an aria-label or a confirm sentence. It prefers `display_name`,
// because those places want the whole phrase and cannot style half of it.
//
// ── THE FALLBACK CHAIN GOES ALL THE WAY DOWN ────────────────────────────────
// A payload predating the name columns still has to render something, so the
// chain ends at the SKU and then at the em-dash placeholder. An ItemName that
// can print nothing is worse than one that prints a code.
//
// ── THE BRAND CHIP, AND WHY IT IS HERE AND NOT IN THE VIEW ──────────────────
// FF-ESC-02-2-6-GC and SD-ESC-02-2-6-GC are two different items that compose to
// the same name: "Gen2 ESC (rev 2.6, Green Circuits)". Nothing in the name
// columns separates them, because the brand prefix is not one of the facts the
// name is built from.
//
// It cannot be fixed in `catalog_item_names`: the answer depends on which
// PRODUCT is running. On a Firefleet deployment the FF rows are the ordinary
// ones and an SD row is the exception worth marking; on SwarmSystems it is the
// other way round. A view is per database and knows nothing about the frontend
// it is being read by, so it would have to mark BOTH and put a chip on every
// row of the common case.
//
// So the component derives the brand from the SKU prefix and marks only what is
// NOT this deployment's own (`DEFAULT_BRAND`, which follows REACT_APP_INSTANCE).
// Same-brand items, which is nearly all of them, show nothing at all. An
// unrecognized prefix shows nothing either: a malformed SKU must not put a
// fragment of itself on screen as though it were a brand, and a short code
// (GMT-7K3Q) carries no brand to begin with.
//
// `itemLabel` carries the same mark as a leading word, because the places it
// lands are the ones where getting the wrong one of two identically named items
// costs the most: a confirm sentence, a toast, an aria-label.

import React from 'react';
import { BRAND_PREFIXES, DEFAULT_BRAND } from '../Parts/skuGrammar';
import Tag from './ui/Tag';

export interface ItemNameSource {
  base_name?: string | null;
  display_name?: string | null;
  variant_detail?: string | null;
  sku?: string | null;
  description?: string | null;
  /** What the joined reads (PO lines, receipts, boxes) call the catalog row's
   *  description. Same fact as `description`, different column name on the way
   *  out, so both are in the chain. */
  part_description?: string | null;
}

export interface ItemNameProps {
  item: ItemNameSource;
  /** Show the SKU as a muted mono line. Detail pages and labels only. */
  showSku?: boolean;
  size?: 'sm' | 'md' | 'lg'; // sm for table cells, md default, lg for page headers
  /** Render inline (span) instead of a block; the detail stays in the same line. */
  inline?: boolean;
  className?: string;
}

/** The empty placeholder, and the only place this glyph is allowed. */
const EMPTY = '—';

/** First value that is actually a string with something in it. `??` alone is
 *  not enough: the API sends '' for a name nobody filled in, and an empty
 *  primary line would silently swallow the fallbacks below it. */
function firstText(...values: (string | null | undefined)[]): string | null {
  for (const v of values) {
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return null;
}

/** The other product's brand code, or null when this item is one of ours. */
function foreignBrandOf(sku: string | null): string | null {
  if (!sku) return null;
  const prefix = sku.split('-')[0]?.toUpperCase() || '';
  if (!(BRAND_PREFIXES as readonly string[]).includes(prefix)) return null;
  return prefix === DEFAULT_BRAND ? null : prefix;
}

/**
 * One flat string for a title, a toast, an aria-label or a confirm sentence.
 *
 * Prefers display_name: those places want the whole phrase, "Gen2 Pyro (V24,
 * rev 1, Green Circuits)", and none of them can render half of it differently.
 *
 * It carries the brand mark for the same reason the chip below does, and by the
 * same rule: FF-ESC-02-2-6-GC and SD-ESC-02-2-6-GC compose to one string, so a
 * confirm sentence naming the wrong one of the two is a person deleting the
 * wrong item. A word rather than the chip's box, because everywhere this string
 * lands is a place that cannot style half of itself.
 */
export function itemLabel(item: ItemNameSource): string {
  const text = firstText(
    item?.display_name,
    item?.base_name,
    item?.part_description,
    item?.description,
    item?.sku
  );
  if (!text) return EMPTY;
  // No special case for a nameless row, where this reads "SD SD-ESC-02-2-6-GC".
  // The chip does not special-case it either, and a label that disagrees with
  // the thing beside it on screen is worse than one that repeats itself.
  const foreign = foreignBrandOf(firstText(item?.sku));
  return foreign ? `${foreign} ${text}` : text;
}

// Size is three decisions, not one, so they are declared together rather than
// derived. `lg` wears the page-title treatment because the only thing that asks
// for it IS a page title, and a detail page whose h1 changed color when an
// item gained a name would read as two different pages.
const SIZES = {
  sm: { name: 'text-sm leading-tight text-on-surface', detail: 'text-sm' },
  md: { name: 'text-sm text-on-surface', detail: 'text-sm' },
  lg: { name: 'text-2xl font-bold text-primary', detail: 'text-base font-normal' },
} as const;

// The SKU line is one style at every size on purpose: it is the same fact
// wherever it appears, and it is never the thing being read first.
const SKU_CLASS = 'font-mono text-xs text-on-surface-variant';

// One size at every size, like the SKU line: a marker, never a headline. It
// reads as a stamp on the name rather than as part of it, so it is the kit's
// code tag (a recessed pill, not an outlined box that reads as a second control).
const BRAND_CHIP_CLASS = 'mr-1.5 align-middle';

const ItemName: React.FC<ItemNameProps> = ({
  item,
  showSku = false,
  size = 'md',
  inline = false,
  className = '',
}) => {
  const sizes = SIZES[size];

  const baseName = firstText(item?.base_name);
  const displayName = firstText(item?.display_name);
  const primary =
    baseName ||
    displayName ||
    firstText(item?.part_description) ||
    firstText(item?.description) ||
    firstText(item?.sku) ||
    EMPTY;

  // display_name ALREADY carries the detail in parentheses. Appending
  // variant_detail to it prints "Gen2 Pyro (V24) (V24)".
  const detail = baseName ? firstText(item?.variant_detail) : null;

  const sku = firstText(item?.sku);
  const foreignBrand = foreignBrandOf(sku);
  // Nothing gains from a SKU line that repeats the line above it, which is what
  // a nameless row would render.
  const skuLine = showSku && sku && sku !== primary ? sku : null;

  const label = itemLabel(item);
  const Wrapper = (inline ? 'span' : 'div') as 'span' | 'div';

  return (
    // The title rides the WRAPPER, never a button: on a button it replaces the
    // accessible name instead of supplementing it.
    <Wrapper className={`${inline ? 'inline' : 'block min-w-0'} ${className}`} title={label}>
      <span className={inline ? sizes.name : `block truncate ${sizes.name}`}>
        {foreignBrand && <Tag variant="code" mono size="sm" className={BRAND_CHIP_CLASS}>{foreignBrand}</Tag>}
        {primary}
        {/* One phrase, one line, at every size. "Gen2 Pyro (V24, rev 1, Green
            Circuits)" is how somebody says it out loud, and breaking it over
            two lines makes the variant read as a separate fact. */}
        {detail && <span className={`text-on-surface-variant ${sizes.detail}`}> ({detail})</span>}
      </span>
      {skuLine && <span className={`block ${SKU_CLASS}`}>{skuLine}</span>}
    </Wrapper>
  );
};

export default ItemName;
